import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, change } from 'redux-form';
import renderToggleButtonField from 'src/shared/components/form/ToggleButton';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import { ERROR_MESSAGES, GRL_URL } from '../../../../../utils/pnd/PndConstants';
import { Modal, ModalBody, ModalHeader, Button } from 'reactstrap';
import { connect } from 'react-redux';
import renderMultiSelectField from 'src/shared/components/form/MultiSelect';
import { pollingIntervalValidation } from 'src/utils/validationUtils';
import renderSelectField from 'src/shared/components/form/Select';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification } from 'src/shared/components/Notification';

// ─── Validators ───────────────────────────────────────────────────────────────
const required = value => value ? undefined : 'Required';
const isNumber = value => (isNaN(value) ? 'Must be a number' : undefined);
const valueLength = value => value && value.length !== 12 ? 'Must be 12 digits' : undefined;
const startWithHash = value => value && (value[0] !== '9' || value[1] !== '7' || value[2] !== '1' || value[3] !== '5') ? 'Must start with 9715' : undefined;
const emailErrorRequired = value => value && /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) ? undefined : 'Enter a valid email';

// ─── Styled Field Wrapper ─────────────────────────────────────────────────────
const renderField = ({ input, label, placeholder, type, meta: { touched, error }, disabled }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <input
            {...input}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                border: touched && error ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0',
                fontSize: '0.85rem',
                outline: 'none',
                background: disabled ? '#f8fafc' : '#fff',
                color: '#1e293b',
                transition: 'border-color 0.2s',
            }}
            onFocus={e => !disabled && (e.target.style.borderColor = '#2563eb')}
            onBlur={e => !disabled && (e.target.style.borderColor = touched && error ? '#ef4444' : '#e2e8f0')}
        />
        {touched && error && <span style={{ fontSize: '0.72rem', color: '#ef4444', fontWeight: 600 }}>{error}</span>}
    </div>
);

// ─── Section Card ─────────────────────────────────────────────────────────────
const SectionCard = ({ title, icon, children, accent = '#2563eb' }) => (
    <div style={{
        background: '#fff',
        borderRadius: 14,
        border: '1px solid #e2e8f0',
        marginBottom: 20,
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
    }}>
        <div style={{
            padding: '12px 18px',
            borderBottom: '1px solid #f1f5f9',
            background: `linear-gradient(135deg, ${accent}10 0%, #fff 100%)`,
            display: 'flex', alignItems: 'center', gap: 10,
            borderTopLeftRadius: 14, borderTopRightRadius: 14,
        }}>
            <span style={{
                width: 32, height: 32, borderRadius: 8,
                background: accent, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: '1rem', flexShrink: 0,
            }}>{icon}</span>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>{title}</span>
        </div>
        <div style={{ padding: '16px 18px' }}>
            {children}
        </div>
    </div>
);

// ─── Field Group ──────────────────────────────────────────────────────────────
const FieldGroup = ({ label, required: req, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {req && <span style={{ color: '#ef4444', marginRight: 3 }}>*</span>}
            {label}
        </label>
        {children}
    </div>
);

// ─── Grid layout helpers ──────────────────────────────────────────────────────
const Grid = ({ cols = 3, children }) => (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '14px 20px' }}>
        {children}
    </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AddOrUpdateClientDetails = ({ setMapData, handleSubmit, dispatch, isOneYearLate, isToday }) => {
    const [paymentPlanOptions, setPaymentPlanOptions] = useState([]);
    const [subscriptionPlanOptions, setSubscriptionPlanOptions] = useState([]);
    const apaFrameRef = useRef(null);
    const [apaIframeUrl] = useState(GRL_URL);
    const [modalOpen, setModalOpen] = useState(false);
    const [isAddress, setAddress] = useState('');
    const [hubCount, setHubCount] = useState('');

    useEffect(() => { getPlanDetails(); }, []);

    const getPlanDetails = async () => {
        try {
            const response = await PndRestApi.GetSubscriptionAndPaymentPlan();
            if (response.status === 200 && response.data.status === 200) {
                if (response?.data?.data?.customerPaymentPlans) {
                    setPaymentPlanOptions(response.data.data.customerPaymentPlans.map(p => ({
                        value: p.paymentPlanId, label: p.planName,
                    })));
                }
                if (response?.data?.data?.customerSubscriptionPlans) {
                    setSubscriptionPlanOptions(response.data.data.customerSubscriptionPlans.map(p => ({
                        value: p.subscriptionPlanId,
                        label: `${p.planName} (${p.allowingCount})`,
                        hubCount: p?.allowedHubCount,
                        operatorCount: p?.allowedHubOperatorCount,
                        deviceCount: p?.allowedDeviceCount,
                    })));
                }
            } else {
                showNotification('theme-light', 'ltr', `Error ${response.data.status}`, response.data.message);
            }
        } catch (e) {
            showNotification('theme-light', 'ltr', 'Error 500', ERROR_MESSAGES.tryCatchError);
        }
    };

    const toggleModal = () => setModalOpen(v => !v);

    const handleMessageToIframe = () => {
        const datajson = '{"RequestID": ' + Math.floor((Math.random() * 20) + 1) + ',"MessageType":1,"AddressElements": [{"AddressElementTypeCode": 1,"Value":""},{"AddressElementTypeCode":2,"Value": ""},{ "AddressElementTypeCode": 3,"Value": ""},{"AddressElementTypeCode":4,"Value": ""},{"AddressElementTypeCode": 5,"Value":"520950"},{"AddressElementTypeCode": 6,"Value":"" },{"AddressElementTypeCode": 7, "Value": "" },{"AddressElementTypeCode": 8,"Value":""}]}';
        if (apaFrameRef.current) {
            apaFrameRef.current.contentWindow.postMessage(datajson, '*');
            window.addEventListener('message', handleMessageFromIframe);
        }
    };

    let deliveryAddress = '';
    const handleMessageFromIframe = (event) => {
        if (event && event.data) {
            try {
                const jsonRegex = /^\s*(\{.*\}|\[.*\])\s*$/;
                if (jsonRegex.test(event.data)) {
                    const d = JSON.parse(event.data);
                    const get = code => d.AddressElements.find(e => e.AddressElementTypeCode === code);
                    const buildingName = get(6), landmark = get(4), street = get(3);
                    const area = get(2), zone = get(7), city = get(8), emirates = get(1);
                    if (buildingName?.Value) deliveryAddress += buildingName.Value + ',';
                    if (!buildingName?.Value && landmark?.Value) deliveryAddress += landmark.Value + ',';
                    if (street) deliveryAddress += street.Value + ',';
                    if (area) deliveryAddress += area.Value + ',';
                    if (zone) deliveryAddress += zone.Value + ',';
                    if (city) deliveryAddress += city.Value + ',';
                    if (emirates) deliveryAddress += emirates.Value;
                    const cleaned = deliveryAddress.replace(/,,/g, ',');
                    setAddress(cleaned);
                    setMapData(cleaned);
                    toggleModal();
                }
            } catch (e) { /* ignore */ }
        }
    };

    const handleHubCountChange = (event) => {
        const value = event?.target?.value;
        setHubCount(value);
        dispatch(change('AddOrUpdateClientDetailsForm', 'hubMangerCount', value));
    };

    const handlePlanChange = (selectedOption) => {
        dispatch(change('AddOrUpdateClientDetailsForm', 'maxDeviceCount', selectedOption.deviceCount));
        dispatch(change('AddOrUpdateClientDetailsForm', 'maxHubCount', selectedOption.hubCount));
        dispatch(change('AddOrUpdateClientDetailsForm', 'hubMangerCount', selectedOption.hubCount));
        dispatch(change('AddOrUpdateClientDetailsForm', 'maxHubOperatorCount', selectedOption.operatorCount));
    };

    return (
        <div style={{ padding: '4px 0' }}>
            {/* ── Page Header ── */}
            <div style={{
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
                borderRadius: 14, padding: '18px 24px', marginBottom: 24,
                display: 'flex', alignItems: 'center', gap: 14,
                boxShadow: '0 4px 20px rgba(37,99,235,0.25)',
            }}>
                <div style={{ background: 'rgba(255,255,255,0.18)', borderRadius: 12, padding: '10px 14px', fontSize: '1.6rem' }}>🏢</div>
                <div>
                    <h4 style={{ margin: 0, color: '#fff', fontWeight: 800, fontSize: '1.2rem' }}>Add Customer / Client</h4>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>Fill in the details below to onboard a new client</p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>

                {/* ── Section 1: Subscription & Plan ── */}
                <SectionCard title="Subscription & Plan" icon="📋" accent="#2563eb">
                    <Grid cols={4}>
                        <FieldGroup label="Subscription Plan" required>
                            <Field
                                validate={[required]}
                                name="customerSubscriptionPlanInfo"
                                component={renderSelectField}
                                type="text"
                                placeholder="Select Plan"
                                options={subscriptionPlanOptions}
                                onChange={handlePlanChange}
                            />
                        </FieldGroup>
                        <FieldGroup label="Payment Plan" required>
                            <Field
                                validate={[required]}
                                name="customerPaymentPlanInfo"
                                component={renderSelectField}
                                type="text"
                                placeholder="Select Payment Plan"
                                options={paymentPlanOptions}
                            />
                        </FieldGroup>
                        <FieldGroup label="Time Zone" required>
                            <Field
                                validate={[required]}
                                name="timeZoneInfo"
                                component={renderSelectField}
                                type="text"
                                placeholder="Select Time Zone"
                                options={[{ value: 1, label: 'Asia/Dubai' }]}
                            />
                        </FieldGroup>
                        <FieldGroup label="Subscription Period">
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input readOnly value={isToday || ''} style={readonlyStyle} placeholder="Start Date" />
                                <input readOnly value={isOneYearLate || ''} style={readonlyStyle} placeholder="End Date" />
                            </div>
                        </FieldGroup>
                    </Grid>
                </SectionCard>

                {/* ── Section 2: Client Information ── */}
                <SectionCard title="Client Information" icon="👤" accent="#7c3aed">
                    <Grid cols={3}>
                        <FieldGroup label="Account No." required>
                            <Field name="accountNo" component={renderField} type="text" placeholder="Enter Account No." validate={[required, isNumber]} />
                        </FieldGroup>
                        <FieldGroup label="Client Name" required>
                            <Field name="clientName" component={renderField} type="text" placeholder="Enter Client Full Name" validate={[required]} />
                        </FieldGroup>
                        <FieldGroup label="Mobile No." required>
                            <Field name="mobileNo" component={renderField} type="text" placeholder="971500000000" validate={[required, valueLength, startWithHash]} />
                        </FieldGroup>
                        <FieldGroup label="Email ID" required>
                            <Field name="emailId" component={renderField} type="text" placeholder="client@example.com" validate={[required, emailErrorRequired]} />
                        </FieldGroup>
                        <FieldGroup label="Telephone No.">
                            <Field name="telephone" component={renderField} type="text" placeholder="Client Telephone No." />
                        </FieldGroup>
                        <FieldGroup label="Operational Status" required>
                            <div style={{ paddingTop: 6 }}>
                                <Field name="status" component={renderToggleButtonField} defaultChecked />
                            </div>
                        </FieldGroup>
                    </Grid>

                    {/* Address full-width row */}
                    <div style={{ marginTop: 14 }}>
                        <FieldGroup label="Address" required>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <input
                                    name="address"
                                    value={isAddress}
                                    readOnly
                                    type="text"
                                    placeholder="Click the pin icon to pick address from map"
                                    style={{ ...readonlyStyle, flex: 1, cursor: 'default' }}
                                />
                                <button type="button" onClick={toggleModal} style={mapBtnStyle} title="Pick from map">
                                    <AddLocationAltIcon style={{ fontSize: '1.3rem' }} />
                                    <span style={{ fontSize: '0.78rem', fontWeight: 700 }}>Pick on Map</span>
                                </button>
                            </div>
                        </FieldGroup>
                    </div>
                </SectionCard>

                {/* ── Section 3: Accounts / Technical Contacts ── */}
                <SectionCard title="Contacts" icon="📞" accent="#059669">
                    <Grid cols={3}>
                        <FieldGroup label="A/C Dept. Contact Name" required>
                            <Field name="clientAccountantName" component={renderField} type="text" placeholder="Accounts Contact Name" validate={[required]} />
                        </FieldGroup>
                        <FieldGroup label="A/C Dept. Contact No." required>
                            <Field name="clientAccountantMobile" component={renderField} type="text" placeholder="971500000000" validate={[required, valueLength, startWithHash]} />
                        </FieldGroup>
                        <FieldGroup label="A/C Dept. Email" required>
                            <Field name="clientAccountantEmail" component={renderField} type="text" placeholder="accounts@example.com" validate={[required, emailErrorRequired]} />
                        </FieldGroup>
                        <FieldGroup label="Technical Contact Name">
                            <Field name="clientTechName" component={renderField} type="text" placeholder="Technical Contact Name" />
                        </FieldGroup>
                        <FieldGroup label="Technical Contact No.">
                            <Field name="clientTechMobileNo" component={renderField} type="text" placeholder="Technical Contact Number" />
                        </FieldGroup>
                    </Grid>
                </SectionCard>

                {/* ── Section 4: Limits & Services ── */}
                <SectionCard title="Limits & Services" icon="⚙️" accent="#d97706">
                    <Grid cols={4}>
                        <FieldGroup label="Allowed Device Count" required>
                            <Field name="maxDeviceCount" component={renderField} type="text" placeholder="Device Count" validate={[required, isNumber]} />
                        </FieldGroup>
                        <FieldGroup label="Hub Count" required>
                            <Field name="maxHubCount" component={renderField} type="text" placeholder="Hub Count" validate={[required, isNumber]} onChange={handleHubCountChange} />
                        </FieldGroup>
                        <FieldGroup label="Hub Manager Count" required>
                            <Field name="hubMangerCount" component={renderField} type="text" placeholder="Hub Manager Count" validate={[required, isNumber]} disabled={true} value={hubCount} />
                        </FieldGroup>
                        <FieldGroup label="Merchant Count" required>
                            <Field name="maxMerchantCount" component={renderField} type="text" placeholder="Merchant Count" validate={[required, isNumber]} />
                        </FieldGroup>
                        <FieldGroup label="Users per Merchant" required>
                            <Field name="perMaxMerchantUserCount" component={renderField} type="text" placeholder="Merchant User Count" validate={[required, isNumber]} />
                        </FieldGroup>
                        <FieldGroup label="Operators per Hub" required>
                            <Field name="maxHubOperatorCount" component={renderField} type="text" placeholder="Hub Operator Count" validate={[required, isNumber]} />
                        </FieldGroup>
                        {/* <FieldGroup label="SMS Count" required>
                            <Field name="maxSmsCount" component={renderField} type="text" placeholder="SMS Count" validate={[required, isNumber]} />
                        </FieldGroup> */}
                        {/* <FieldGroup label="Supporting Services" required>
                            <Field
                                validate={[required]}
                                name="supportingServices"
                                component={renderMultiSelectField}
                                type="text"
                                options={[
                                    { value: 1, label: 'Pick Up' },
                                    { value: 2, label: 'Drop Off' },
                                    { value: 3, label: 'PicknDrop' },
                                ]}
                                placeholder="Select Services"
                            />
                        </FieldGroup> */}
                    </Grid>
                </SectionCard>

                {/* ── Section 5: Polling Configuration ── */}
                <SectionCard title="Polling Configuration" icon="🔄" accent="#0891b2">
                    <Grid cols={3}>
                        <FieldGroup label="Active Polling Interval (sec)" required>
                            <Field name="activePollingInterval" component={renderField} type="text" placeholder="e.g. 30" validate={[pollingIntervalValidation, required]} />
                        </FieldGroup>
                        <FieldGroup label="In-Active Polling Interval (sec)" required>
                            <Field name="inactivePollingInterval" component={renderField} type="text" placeholder="e.g. 60" validate={[pollingIntervalValidation, required]} />
                        </FieldGroup>
                        <FieldGroup label="Delivery Polling Interval (sec)" required>
                            <Field name="deliveryPollingInterval" component={renderField} type="text" placeholder="e.g. 15" validate={[pollingIntervalValidation, required]} />
                        </FieldGroup>
                    </Grid>
                </SectionCard>

                {/* ── Submit ── */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 24 }}>
                    <button
                        type="submit"
                        style={{
                            padding: '10px 32px',
                            borderRadius: 10,
                            border: 'none',
                            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            boxShadow: '0 4px 14px rgba(37,99,235,0.35)',
                            transition: 'all 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'linear-gradient(135deg, #1d4ed8, #1e3a8a)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb, #1d4ed8)'}
                    >
                        ✅ Submit Client
                    </button>
                </div>
            </form>

            {/* ── Map Modal ── */}
            <Modal isOpen={modalOpen} toggle={toggleModal} style={{ maxWidth: 1000 }}>
                <ModalHeader toggle={toggleModal}><b>📍 Find Location</b></ModalHeader>
                <ModalBody style={{ padding: 0 }}>
                    <iframe
                        ref={apaFrameRef}
                        src={apaIframeUrl}
                        title="Get Address"
                        onLoad={handleMessageToIframe}
                        style={{ width: '100%', height: 650, border: 'none' }}
                    />
                </ModalBody>
            </Modal>
        </div>
    );
};

// ─── Shared style constants ───────────────────────────────────────────────────
const readonlyStyle = {
    padding: '8px 12px', borderRadius: 8,
    border: '1.5px solid #e2e8f0', fontSize: '0.85rem',
    background: '#f8fafc', color: '#64748b', outline: 'none',
};

const mapBtnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 8,
    border: '1.5px solid #16a34a', background: '#f0fdf4',
    color: '#16a34a', cursor: 'pointer', whiteSpace: 'nowrap',
    fontWeight: 700, transition: 'all 0.2s',
};

AddOrUpdateClientDetails.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
};

const mapStateToProps = (state, data) => {
    if (data && data?.isComServerData) {
        return {
            initialValues: {
                maxSmsCount: 1000,
                inactivePollingInterval: data.isComServerData?.inactivePollingInterval,
                deliveryPollingInterval: data.isComServerData?.deliveryPollingInterval,
                activePollingInterval: data.isComServerData?.activePollingInterval,
            }
        };
    }
};

export default connect(mapStateToProps)(reduxForm({ form: 'AddOrUpdateClientDetailsForm', enableReinitialize: true })(AddOrUpdateClientDetails));